import React, { useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useIndexedDBReportLoader, useIndexedDBPageQuery, useReportTotals } from './dataIndexedDB'

const DDPReportIndexedDBContext = React.createContext({})

export const useDDPReportIndexedDBContext = () => {
  return useContext(DDPReportIndexedDBContext)
}

const useIndexedDBReportManager = (pipeline, variables, hierarchy, userId, maxAge, concurrency, chunkSize, columnConfig) => {
  const [globalFilter, setGlobalFilter] = useState('')
  const [loadingProgress, setLoadingProgress] = useState(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(250)
  const [sortBy, setSortBy] = useState(null)
  const [sortDesc, setSortDesc] = useState(false)
  const [expandedRows, setExpandedRows] = useState({})

  // Build map of column id → accessorKey for sort mapping
  const columnIdMap = useMemo(() => {
    if (!columnConfig) return {}
    const map = {}
    columnConfig.forEach(col => {
      const id = col.id || col.accessorKey || col.accessor
      const accessorKey = col.accessorKey || col.accessor
      if (id && accessorKey) {
        map[id] = accessorKey
      }
    })
    return map
  }, [columnConfig])

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setPageIndex(0)
  }, [globalFilter])

  const dataQuery = useMemo(() => {
    return {
      pipeline: pipeline,
      variables: variables,
      hierarchy: hierarchy
    }
  }, [pipeline, variables, hierarchy])

  const handleProgress = useCallback((progress) => {
    setLoadingProgress(progress)
  }, [])

  // Load/sync with IndexedDB
  const {
    data: loaderData,
    isLoading: isLoadingData,
    error: loaderError
  } = useIndexedDBReportLoader(
    dataQuery,
    userId,
    handleProgress,
    maxAge,
    concurrency,
    chunkSize
  )

  // Query current page from IndexedDB
  const pageOptions = useMemo(() => {
    // Map column id to accessorKey for sorting
    const actualSortBy = sortBy ? (columnIdMap[sortBy] || sortBy) : null

    return {
      offset: pageIndex * pageSize,
      limit: pageSize,
      searchTerm: globalFilter,
      sortBy: actualSortBy, // Use mapped accessorKey
      sortDesc
    }
  }, [pageIndex, pageSize, globalFilter, sortBy, sortDesc, columnIdMap])

  // Only enable page query after loader completes (data is in IndexedDB)
  const enablePageQuery = !isLoadingData && !!loaderData

  const {
    data: pageData,
    isLoading: isLoadingPage,
    isFetching: isFetchingPage
  } = useIndexedDBPageQuery(
    dataQuery,
    userId,
    pageOptions,
    enablePageQuery // Wait for data to be stored
  )

  // Fetch totals row (separate query)
  const {
    data: totalsData,
    isLoading: isLoadingTotals
  } = useReportTotals(dataQuery, userId, enablePageQuery)

  // Navigate to page
  const goToPage = useCallback((newPageIndex) => {
    setPageIndex(newPageIndex)
  }, [])

  const changePageSize = useCallback((newPageSize) => {
    setPageSize(newPageSize)
    setPageIndex(0) // Reset to first page
  }, [])

  const changeSorting = useCallback((updaterOrValue) => {
    // TanStack Table passes an updater function, not the value directly
    const currentSort = sortBy ? [{ id: sortBy, desc: sortDesc }] : []
    const newSortState = typeof updaterOrValue === 'function'
      ? updaterOrValue(currentSort)
      : updaterOrValue

    if (newSortState && newSortState.length > 0) {
      setSortBy(newSortState[0].id)
      setSortDesc(!!newSortState[0].desc)
    } else {
      setSortBy(null)
      setSortDesc(false)
    }
    setPageIndex(0) // Reset to first page on sort
  }, [sortBy, sortDesc])

  const toggleRowExpanded = useCallback((rowKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }))
  }, [])

  return useMemo(() => {
    return {
      pipeline,
      originalVariables: variables,
      hierarchy,
      userId,
      columnConfig, // Add columnConfig to context

      // Data state
      data: pageData?.data || [],
      totalRows: pageData?.total || 0,
      totals: totalsData || [],
      isLoading: isLoadingData || isLoadingPage || isLoadingTotals,
      isFetching: isFetchingPage,
      error: loaderError,

      // Pagination state
      pageIndex,
      pageSize,
      goToPage,
      changePageSize,

      // Sort state
      sortBy,
      sortDesc,
      changeSorting,

      // Expand state
      expandedRows,
      toggleRowExpanded,

      // Filter state
      globalFilter,
      setGlobalFilter,

      // Loading progress
      loadingProgress,

      // Metadata
      source: loaderData?.source,
      metadata: loaderData?.metadata
    }
  }, [
    pipeline,
    variables,
    hierarchy,
    userId,
    columnConfig,
    pageData,
    totalsData,
    isLoadingData,
    isLoadingPage,
    isLoadingTotals,
    isFetchingPage,
    loaderError,
    pageIndex,
    pageSize,
    goToPage,
    changePageSize,
    sortBy,
    sortDesc,
    changeSorting,
    expandedRows,
    toggleRowExpanded,
    globalFilter,
    loadingProgress,
    loaderData
  ])
}

export function DDPReportIndexedDBProvider ({
  pipeline,
  variables,
  hierarchy,
  columnConfig,
  maxAge,
  concurrency,
  chunkSize,
  children
}) {
  const { userId } = useAppContext()
  const reportManager = useIndexedDBReportManager(
    pipeline,
    variables,
    hierarchy,
    userId,
    maxAge,
    concurrency,
    chunkSize,
    columnConfig // Pass columnConfig for id → accessorKey mapping
  )

  return (
    <DDPReportIndexedDBContext.Provider value={reportManager}>
      {children}
    </DDPReportIndexedDBContext.Provider>
  )
}

DDPReportIndexedDBProvider.propTypes = {
  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  /** List of variables */
  variables: PropTypes.object,

  /** How to transform flat data into hierarchal */
  hierarchy: PropTypes.object,

  /** Column configuration (for id → accessorKey mapping) */
  columnConfig: PropTypes.array,

  /** Cache max age in ms (default: 1 hour) */
  maxAge: PropTypes.number,

  /** Number of concurrent chunk requests (default: 4) */
  concurrency: PropTypes.number,

  /** Rows per chunk (default: 10000) */
  chunkSize: PropTypes.number,

  children: PropTypes.node
}

DDPReportIndexedDBProvider.defaultProps = {
  maxAge: 24 * 60 * 60 * 1000, // 24 hour
  concurrency: 4,
  chunkSize: 10000
}

export default DDPReportIndexedDBProvider
